import type { Metadata } from "next"

// Enhanced SEO configuration
export const siteConfig = {
  name: "Real Metro Metal",
  description:
    "Real Metro Metal is Sri Lanka's leading wholesale hardware supplier, specializing in high-quality industrial materials for construction and manufacturing. Based in Old Moor Street, Colombo, we provide an extensive range of premium products including industrial valves, stainless steel pipes, GI conduits, H-iron beams, box bars, and mild steel sheets.",
  url: "https://realmetrometal.com",
  ogImage: "/image/cover.png",
  creator: "Real Metro Metal",
  keywords: [
    "wholesale hardware supplier Sri Lanka",
    "industrial hardware Colombo",
    "Old Moor Street hardware",
    "valves pipes plates sheets Sri Lanka",
    "H-iron boxbars nuts bolts",
    "GI conduits fittings wholesale",
    "construction hardware supplier Colombo",
    "metal products Sri Lanka",
    "industrial supplies Colombo",
    "hardware wholesale prices Sri Lanka",
    "quality industrial hardware Colombo",
  ],
  authors: [
    {
      name: "Real Metro Metal",
      url: "https://realmetrometal.com",
    },
  ],
}

export function generateSEOMetadata({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  publishedTime,
  modifiedTime,
}: {
  title?: string
  description?: string
  keywords?: string[]
  image?: string
  url?: string
  type?: "website" | "article"
  publishedTime?: string
  modifiedTime?: string
}): Metadata {
  const seoTitle = title 
    ? (title.includes(siteConfig.name) ? title : `${title} | ${siteConfig.name}`) 
    : siteConfig.name
  const seoDescription = description || siteConfig.description
  const seoImage = image
    ? (image.startsWith('http') ? image : `${siteConfig.url}${image.startsWith('/') ? '' : '/'}${image}`)
    : `${siteConfig.url}${siteConfig.ogImage}`

  const seoUrl = url
    ? (url.startsWith('http') ? url : `${siteConfig.url}${url.startsWith('/') ? '' : '/'}${url}`)
    : siteConfig.url
  const seoKeywords = keywords ? [...siteConfig.keywords, ...keywords] : siteConfig.keywords

  return {
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords.join(", "),
    authors: siteConfig.authors,
    creator: siteConfig.creator,
    openGraph: {
      type,
      locale: "en_US",
      url: seoUrl,
      title: seoTitle,
      description: seoDescription,
      siteName: siteConfig.name,
      images: [
        {
          url: seoImage,
          width: 1200,
          height: 630,
          alt: seoTitle,
        },
      ],
      ...(publishedTime && { publishedTime }),
      ...(modifiedTime && { modifiedTime }),
    },
    twitter: {
      card: "summary_large_image",
      title: seoTitle,
      description: seoDescription,
      images: [seoImage],
      creator: "@realmetrometal",
    },
    robots: {
      index: true,
      follow: true,
      nocache: false,
      googleBot: {
        index: true,
        follow: true,
        noimageindex: false,
        "max-video-preview": -1,
        "max-image-preview": "large",
        "max-snippet": -1,
      },
    },
    alternates: {
      canonical: seoUrl,
    },
  }
}

// Structured data generators
export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Real Metro Metal",
    alternateName: "RMM Hardware",
    url: "https://realmetrometal.com",
    logo: "https://realmetrometal.com/logo.png",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+94-77-393-3643",
      contactType: "customer service",
      areaServed: "LK",
      availableLanguage: ["en", "si"],
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "392 1/3 Old Moor St",
      addressLocality: "Colombo",
      postalCode: "01200",
      addressCountry: "LK",
    },
    sameAs: ["https://realmetrometal.com"],
  }
}

export function generateLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://realmetrometal.com/#business",
    name: "Real Metro Metal",
    image: "https://realmetrometal.com/image/cover.png",
    telephone: "+94-77-393-3643",
    address: {
      "@type": "PostalAddress",
      streetAddress: "392 1/3 Old Moor St",
      addressLocality: "Colombo",
      postalCode: "01200",
      addressCountry: "LK",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 6.9271,
      longitude: 79.8612,
    },
    url: "https://realmetrometal.com",
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "08:00",
        closes: "18:00",
      },
    ],
    priceRange: "$$",
    servesCuisine: "Industrial Hardware",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "127",
    },
  }
}

export function generateProductSchema(product: any) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: product.image,
    description: product.meta.description,
    brand: {
      "@type": "Brand",
      name: "Real Metro Metal",
    },
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      priceCurrency: "LKR",
      seller: {
        "@type": "Organization",
        name: "Real Metro Metal",
      },
    },
    category: product.category,
    additionalProperty: [
      {
        "@type": "PropertyValue",
        name: "Category",
        value: product.category,
      },
      ...(product.subcategory
        ? [
          {
            "@type": "PropertyValue",
            name: "Subcategory",
            value: product.subcategory,
          },
        ]
        : []),
    ],
  }
}

export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}
